import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BadgeIndianRupee, Palette, Smartphone, HeartHandshake, Search, LifeBuoy } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const HIGHLIGHTS = [
  { icon: BadgeIndianRupee, t: "Affordable Website Designing" },
  { icon: Palette, t: "Creative & Modern Designs" },
  { icon: Smartphone, t: "Mobile-Friendly Websites" },
  { icon: HeartHandshake, t: "Customer-Focused Solutions" },
  { icon: Search, t: "SEO-Friendly Development" },
  { icon: LifeBuoy, t: "Reliable Customer Support" },
];

const Visual = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <div ref={ref} className="relative">
      <div className="relative aspect-[4/5] overflow-hidden rounded-[28px] border border-white/10 [clip-path:polygon(0_0,100%_0,100%_88%,86%_100%,0_100%)]">
        <motion.img style={{ y, scale: 1.15 }} src="/images/designer.jpg" alt="Web designer working on a laptop in a dark studio" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-transparent to-royal/20" />
        <div className="absolute bottom-8 left-8 right-16">
          <p className="font-display text-2xl font-medium leading-tight text-white">Professional Websites. <span className="text-gold-grad">Affordable Prices.</span> Powerful Results.</p>
        </div>
      </div>
      <div className="glass absolute -right-3 -top-6 hidden w-44 overflow-hidden rounded-2xl p-2 sm:block">
        <img src="/images/studio.jpg" alt="Design studio desk with laptop" className="aspect-square w-full rounded-xl object-cover" loading="lazy" />
      </div>
    </div>
  );
};

export const About = () => (
  <section id="about" data-testid="about-section" className="relative py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
      <Reveal><Visual /></Reveal>
      <div>
        <SectionHead eyebrow="About Pratap Group" title={<>Welcome to Pratap Group – <span className="text-gold-grad italic">Your Digital Growth Partner</span></>} />
        <Reveal delay={0.1}>
          <p className="mt-8 text-base leading-relaxed text-slate-300 md:text-lg">
            At Pratap Group, we believe every business deserves a beautiful and professional online presence without spending a fortune. We specialise in creating modern, responsive, user-friendly websites that help businesses attract customers, establish credibility, and grow online.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-base leading-relaxed text-[var(--muted)]">
            Whether you are a startup, small business, clinic, salon, restaurant, educational institute, or established company, we provide customised web design solutions tailored to your business requirements and budget.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-3 sm:grid-cols-2">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.t} delay={0.05 * i}>
              <div data-testid={`about-highlight-${i}`} className="group flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 transition-[border-color,background-color] duration-300 hover:border-gold/40 hover:bg-white/[0.04]">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-royal/15 text-[var(--royal-2)] transition-colors duration-300 group-hover:bg-gold/15 group-hover:text-[var(--gold)]">
                  <h.icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-medium text-white">{h.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
